import { useEffect, useState } from "react";
import { Brain, CheckCircle2, Loader2, RefreshCw, AlertTriangle, FileText, ShieldCheck, Activity, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const CAPABILITIES = [
  { icon: Brain, label: "Quotes", detail: "Verified REST quote workflow", key: "quotes" },
  { icon: FileText, label: "Applications", detail: "Sync when InstaBrain endpoint is verified", key: "applications" },
  { icon: Activity, label: "Application events", detail: "Event-history sync when endpoint is verified", key: "events" },
  { icon: ShieldCheck, label: "Policies", detail: "Policy sync when endpoint is verified", key: "policies" },
  { icon: BarChart3, label: "Production", detail: "Premium / policy production sync when endpoint is verified", key: "production" },
];

export default function InstaBrainIntegrationOverview() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(false);

  const load = () => api.get("/instabrain/status")
    .then(({ data }) => setStatus(data))
    .catch(() => setStatus({ configured: false, environment: "unknown", products: [] }));

  useEffect(() => { load(); }, []);

  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/instabrain/verify");
      setVerified(!!data?.authenticated);
      toast.success(`InstaBrain ${data?.environment || ""} authentication verified`);
      load();
    } catch (err) {
      setVerified(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "InstaBrain authentication failed");
    } finally { setBusy(false); }
  };

  if (!status) return <div className="mb-5 h-36 animate-pulse rounded-xl border bg-muted/40" />;

  return (
    <section className="mb-5 rounded-xl border border-[#B28A3D]/35 bg-card p-5 shadow-sm" data-testid="instabrain-overview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Brain className="h-5 w-5 text-[#B28A3D]" />
            <h2 className="font-heading text-lg font-bold">InstaBrain · Primary Placement & Application Partner</h2>
            <Badge className={status.configured ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
              {status.configured ? `${status.environment || ""} configured` : "Credentials required"}
            </Badge>
            {verified && <Badge variant="outline" className="text-emerald-700"><CheckCircle2 className="mr-1 h-3 w-3" />Authenticated</Badge>}
          </div>
          <p className="mt-1 max-w-4xl text-sm text-muted-foreground">
            Fortunate Ops uses InstaBrain for placement and quoting today, and keeps application, event, policy and production sync visible as the next verified API scope instead of hiding the integration behind a quote button.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={verify} disabled={busy || !status.configured} data-testid="instabrain-overview-verify">
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Verify Connection
        </Button>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        {CAPABILITIES.map(({ icon: Icon, label, detail, key }) => {
          const live = key === "quotes" && status.configured;
          return <div key={key} className="rounded-lg border bg-background p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold"><Icon className="h-3.5 w-3.5 text-[#315A7D]" />{label}</div>
              <Badge variant="outline" className={live ? "text-emerald-700" : "text-muted-foreground"}>{live ? "Available" : "Verify API"}</Badge>
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{detail}</p>
          </div>;
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span className="font-semibold text-foreground">Current verified quote products:</span>
        {(status.products || []).map((p) => <Badge key={p.key} variant="secondary">{p.label}</Badge>)}
        {(status.products || []).length === 0 && <span>Connection required</span>}
      </div>
      <div className="mt-3 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>Fortunate Ops will not invent private InstaBrain endpoints. Application, event, policy and production sync will activate only after those endpoints are verified with the authorized InstaBrain API credential.</span>
      </div>
    </section>
  );
}
